/**
 * SafePassage Design Tokens
 * Comprehensive design system for consistent UI across all screens
 *
 * Version: 3.0 - Simplified Breakpoint System
 * - Simplified to 3 breakpoints: Mobile (up to 768px), Tablet (768px-1024px), Desktop (1024px+)
 * - Removed excessive breakpoints (xs, sm, xl, xxl, xxxl) for better maintainability
 * - Stopped scaling at desktop (1024px+) to prevent over-sizing
 * - Enhanced spacing scale (5xl: 96px, 6xl: 128px) maintained
 * - Extended typography scale (5xl-7xl: 48px-64px) maintained
 * - Expanded icon sizes (4xl: 96px, 5xl: 128px) maintained
 */

// Spacing scale based on 8px grid system (industry standard)
export const spacing = {
  xs: '4px', // Half-unit for fine adjustments
  sm: '8px', // Base unit
  md: '16px', // 2x base
  lg: '24px', // 3x base
  xl: '32px', // 4x base
  '2xl': '48px', // 6x base
  '3xl': '64px', // 8x base - maximum for most uses
  // Removed 4xl, 5xl, 6xl - excessive for standard layouts
} as const;

// Typography scale - Following industry best practices
export const typography = {
  fontFamily: {
    primary:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    mono: "Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace",
  },
  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px', // Minimum for body text (accessibility)
    md: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px', // Mobile heading start
    '3xl': '28px', // Tablet heading
    '4xl': '32px', // Desktop heading start
    '5xl': '36px', // Desktop heading large
    '6xl': '40px', // Maximum heading size
    // Removed '7xl' - 64px is excessive for standard UX
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.7,
  },
} as const;

// Default palette (dark fallback)
export const colors = {
  // Background Colors (from SafePassage palette)
  background: {
    primary: 'var(--verify-bg-primary, #1F2937)',    // gray-800 - Main content background  
    secondary: 'var(--verify-bg-secondary, #374151)',  // gray-700 - Cards and elevated surfaces
    tertiary: 'var(--verify-bg-tertiary, #4B5563)',   // gray-600 - Input fields and tertiary surfaces
    overlay: 'var(--verify-bg-overlay, #111827)',    // gray-900 - Modals and overlays
    sidebar: 'var(--verify-bg-sidebar, #2D3748)',    // Custom dark blue-gray for sidebar
    nav: 'var(--verify-bg-nav, #1A202C)',       // Even darker for nav elements
    modal: 'var(--verify-bg-modal, #111827)',     // gray-900 - Modal backgrounds
  },

  // Text Colors (from SafePassage palette)
  text: {
    primary: 'var(--verify-text-primary, #F3F4F6)',    // gray-100 - Primary text
    secondary: 'var(--verify-text-secondary, #9CA3AF)',  // gray-400 - Secondary/descriptive text
    tertiary: 'var(--verify-text-tertiary, #6B7280)',   // gray-500 - Muted/disabled text
    inverse: 'var(--verify-text-inverse, #111827)',    // gray-900 - Text on light backgrounds
    muted: 'var(--verify-text-muted, #6B7280)',      // gray-500 - Muted/disabled text
    accent: 'var(--brand-primary, #2563eb)', // Brand primary (links, highlights)
    buttonPrimary: 'var(--verify-button-primary-fg, #ffffff)', // White text for primary buttons on brand backgrounds
  },

  // Interactive Colors (from SafePassage palette)
  blue: {
    // Blue (Primary Actions, Information)
    bg: 'var(--brand-primary-light, rgba(37, 99, 235, 0.2))',
    bgHover: 'var(--brand-primary-light-hover, rgba(37, 99, 235, 0.3))',
    border: 'var(--brand-primary-medium, rgba(37, 99, 235, 0.5))',
    text: 'var(--brand-primary, #2563eb)',
    textLight: 'var(--brand-primary, #2563eb)',
    solid: 'var(--brand-primary, #2563eb)',
    solidHover: 'var(--brand-primary-hover, #1d4ed8)',
  },

  green: {
    // Green (Success, Active, Safe/Public)
    bg: 'var(--verify-green-bg, rgba(20, 83, 45, 0.2))',       // bg-green-900/20
    bgHover: 'var(--verify-green-bg-hover, rgba(20, 83, 45, 0.3))',  // Slightly more opacity on hover
    border: 'var(--verify-green-border, rgba(22, 101, 52, 0.5))',  // border-green-800/50
    text: 'var(--verify-green-text, #4ADE80)',                    // text-green-400
    textLight: 'var(--verify-green-text-light, #86EFAC)',               // text-green-300
    badge: 'var(--verify-green-badge, #86EFAC)',                   // text-green-300 for badges
  },

  yellow: {
    // Yellow (Warnings, Special Roles) - Updated for better contrast on dark backgrounds
    bg: 'var(--verify-yellow-bg, rgba(180, 83, 9, 0.35))',           // amber-700, 35% opacity - more visible
    bgHover: 'var(--verify-yellow-bg-hover, rgba(180, 83, 9, 0.45))', // hover state
    border: 'var(--verify-yellow-border, rgba(217, 119, 6, 0.6))',   // amber-600, more visible border
    text: 'var(--verify-yellow-text, #FEF3C7)',                      // amber-100 - light cream for contrast
    textLight: 'var(--verify-yellow-text-light, #FDE68A)',           // amber-200 - slightly darker
  },

  red: {
    // Red (Errors, Destructive Actions)
    bg: 'var(--verify-red-bg, rgba(127, 29, 29, 0.2))',      // bg-red-900/20
    bgHover: 'var(--verify-red-bg-hover, rgba(127, 29, 29, 0.3))',  // Slightly more opacity on hover
    border: 'var(--verify-red-border, rgba(153, 27, 27, 0.5))',  // border-red-800/50
    text: 'var(--verify-red-text, #F87171)',                    // text-red-400
    textLight: 'var(--verify-red-text-light, #FCA5A5)',               // text-red-300
  },

  // Special Elements
  code: {
    background: 'var(--verify-code-bg, #1E293B)',  // slate-800
    border: 'var(--verify-code-border, #334155)',      // slate-700
    text: 'var(--verify-code-text, #E2E8F0)',        // slate-200
  },

  // Gray Scale (from SafePassage palette)
  gray: {
    100: 'var(--verify-gray-100, #F3F4F6)',   // gray-100
    400: 'var(--verify-gray-400, #9CA3AF)',   // gray-400
    500: 'var(--verify-gray-500, #6B7280)',   // gray-500
    600: 'var(--verify-gray-600, #4B5563)',   // gray-600
    700: 'var(--verify-gray-700, #374151)',   // gray-700
    800: 'var(--verify-gray-800, #1F2937)',   // gray-800
    900: 'var(--verify-gray-900, #111827)',   // gray-900
  },

  // Border Colors
  border: {
    default: 'var(--verify-border-default, rgba(255, 255, 255, 0.1))',
    light: 'var(--verify-border-light, rgba(255, 255, 255, 0.05))',
    focus: 'var(--brand-primary, #2563eb)',
    error: 'var(--verify-border-error, #F87171)',      // red-400
    blue: 'var(--verify-border-blue, rgba(30, 64, 175, 0.5))',    // border-blue-800/50
    green: 'var(--verify-border-green, rgba(22, 101, 52, 0.5))',   // border-green-800/50
    yellow: 'var(--verify-border-yellow, rgba(217, 119, 6, 0.6))',  // amber-600/60 - more visible
    red: 'var(--verify-border-red, rgba(153, 27, 27, 0.5))',     // border-red-800/50
  },

  // Legacy semantic colors (maintained for backwards compatibility)
  semantic: {
    success: 'var(--verify-semantic-success, #4ADE80)',   // green-400
    warning: 'var(--verify-semantic-warning, #FACC15)',   // yellow-400  
    error: 'var(--verify-semantic-error, #F87171)',     // red-400
    info: 'var(--brand-primary, #2563eb)',
  },

  // Legacy brand colors (maintained for backwards compatibility)
  brand: {
    primary: 'var(--brand-primary, #2563eb)',
    primaryHover: 'var(--brand-primary-hover, #1d4ed8)',
    primaryActive: 'var(--brand-primary-active, #1e40af)',
  },

  // Legacy neutral colors (maintained for backwards compatibility)
  neutral: {
    white: 'var(--verify-neutral-white, #ffffff)',
    black: 'var(--verify-neutral-black, #000000)',
    elevated: 'var(--verify-neutral-elevated, rgba(255, 255, 255, 0.05))',
    border: 'var(--verify-neutral-border, rgba(255, 255, 255, 0.1))',
  },

  // Opacity utilities for consistent color calculations
  withOpacity: {
    // Blue colors with opacity (updated to SafePassage palette)
    blueLight: 'var(--verify-blue-light, rgba(30, 58, 138, 0.2))',    // bg-blue-900/20
    blueMedium: 'var(--verify-blue-medium, rgba(30, 64, 175, 0.5))',   // border-blue-800/50
    blueFocus: 'var(--verify-blue-focus, rgba(96, 165, 250, 0.2))',   // blue-400 with 20% opacity
    
    // Green colors with opacity (updated to SafePassage palette)
    greenLight: 'var(--verify-green-light, rgba(20, 83, 45, 0.2))',    // bg-green-900/20
    greenMedium: 'var(--verify-green-medium, rgba(22, 101, 52, 0.5))',  // border-green-800/50
    
    // Yellow colors with opacity (updated for better visibility)
    yellowLight: 'var(--verify-yellow-light, rgba(180, 83, 9, 0.35))',  // amber-700/35
    yellowMedium: 'var(--verify-yellow-medium, rgba(217, 119, 6, 0.6))', // amber-600/60
    
    // Red colors with opacity (updated to SafePassage palette)
    errorLight: 'var(--verify-error-light, rgba(127, 29, 29, 0.2))',   // bg-red-900/20
    errorMedium: 'var(--verify-error-medium, rgba(153, 27, 27, 0.5))',  // border-red-800/50
    
    // Neutral colors with opacity
    whiteLight: 'var(--verify-white-light, rgba(255, 255, 255, 0.2))',
    whiteMedium: 'var(--verify-white-medium, rgba(255, 255, 255, 0.5))',
    blackLight: 'var(--verify-black-light, rgba(0, 0, 0, 0.2))',
    blackMedium: 'var(--verify-black-medium, rgba(0, 0, 0, 0.5))',
    
    // Legacy brand colors with opacity (maintained for backwards compatibility)
    brandLight: 'var(--brand-primary-light, rgba(37, 99, 235, 0.2))',
    brandMedium: 'var(--brand-primary-medium, rgba(37, 99, 235, 0.5))',
    brandFocus: 'var(--brand-primary-focus, rgba(37, 99, 235, 0.2))',
    
    // Legacy semantic colors with opacity (maintained for backwards compatibility)
    successLight: 'var(--verify-success-light, rgba(74, 222, 128, 0.2))', // green-400 with 20% opacity
    successMedium: 'var(--verify-success-medium, rgba(74, 222, 128, 0.5))', // green-400 with 50% opacity
    
    warningLight: 'var(--verify-warning-light, rgba(250, 204, 21, 0.2))', // yellow-400 with 20% opacity
    warningMedium: 'var(--verify-warning-medium, rgba(250, 204, 21, 0.5))', // yellow-400 with 50% opacity
  },
} as const;

// Border radius values
export const borderRadius = {
  none: '0',
  xs: '2px',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '24px',
  full: '9999px',
} as const;

// Shadow/elevation system (updated for dark theme)
export const shadows = {
  sm: 'var(--verify-shadow-sm, 0 1px 2px 0 rgba(0, 0, 0, 0.3))',
  md: 'var(--verify-shadow-md, 0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3))',
  lg: 'var(--verify-shadow-lg, 0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3))',
  xl: 'var(--verify-shadow-xl, 0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3))',
  inner: 'var(--verify-shadow-inner, inset 0 2px 4px rgba(0, 0, 0, 0.06))',
} as const;

// Z-index scale
export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  fixed: 300,
  modalBackdrop: 400,
  modal: 500,
  popover: 600,
  tooltip: 700,
  notification: 800,
} as const;

// Simplified responsive breakpoints (Mobile, Tablet, Desktop)
export const breakpoints = {
  // Core 3-breakpoint system
  md: '768px', // Tablet breakpoint
  lg: '1024px', // Desktop breakpoint

  // Numeric values for calculations
  mobile: 320, // Mobile baseline
  tablet: 768, // Tablet breakpoint
  desktop: 1024, // Desktop breakpoint (no further scaling)
} as const;

// Simplified media queries (Mobile, Tablet, Desktop)
export const media = {
  mobile: `@media (max-width: ${breakpoints.tablet - 1}px)`, // Up to 767px
  tablet: `@media (min-width: ${breakpoints.tablet}px) and (max-width: ${breakpoints.desktop - 1}px)`, // 768px-1023px
  desktop: `@media (min-width: ${breakpoints.desktop}px)`, // 1024px+ (no further scaling)
} as const;

// Layout constants
export const layout = {
  maxWidth: {
    // Mobile-optimized widths
    narrow: '400px', // For mobile and small content
    content: '480px', // Standard mobile content width
    wide: '600px', // Large mobile/small tablet

    // Enhanced desktop-optimized widths
    desktop: '900px', // Enhanced desktop content width (from 800px)
    desktopWide: '1100px', // Enhanced wide desktop content (from 1000px)
    desktopXWide: '1300px', // NEW: Extra-wide desktop content
    container: '1200px', // Maximum container width
  },
  containerPadding: {
    mobile: spacing.md, // 16px for mobile
    tablet: spacing.lg, // 24px for tablet
    desktop: spacing.xl, // 32px for desktop (no further scaling)
  },

  verticalRhythm: {
    section: spacing['2xl'],
    element: spacing.lg,
    compact: spacing.md,
  },
  logo: {
    height: {
      mobile: '40px',
      desktop: '48px',
    },
    maxWidth: '280px',
    marginBottom: spacing.xl,
  },
} as const;

// Animation durations
export const transitions = {
  fast: '150ms',
  normal: '300ms',
  slow: '500ms',
  easing: {
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;

// Component-specific tokens
export const components = {
  button: {
    borderRadius: borderRadius['2xl'],
    width: {
      mobile: '90%',
      tablet: '422px',
      desktop: '422px',
    },
    height: '70px',
    padding: {
      sm: `${spacing.sm} ${spacing.lg}`,
      md: `${spacing.md} ${spacing.xl}`,
      lg: `${spacing.lg} ${spacing['2xl']}`,
    },
    fontSize: {
      sm: typography.fontSize.sm,
      md: typography.fontSize.base,
      lg: typography.fontSize.lg,
    },
    fontWeight: typography.fontWeight.semibold,
    transition: `all ${transitions.normal} ${transitions.easing.default}`,
  },
  card: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    background: colors.background.secondary,
    border: `1px solid ${colors.border.default}`,
  },
  imageContainer: {
    maxWidth: {
      sm: '280px', // Small images/thumbnails
      md: '360px', // Medium images (default)
      lg: '420px', // Large images
    },
  },
  icon: {
    size: {
      xs: '16px',
      sm: '20px',
      md: '24px',
      lg: '32px',
      xl: '48px',
      '2xl': '64px',
      '3xl': '80px',
      // Enhanced desktop icon sizes
      '4xl': '96px',
      '5xl': '128px',
      // Maximum desktop icon size (no further scaling beyond 1024px)
      '6xl': '144px',
    },
  },
} as const;

// Export all tokens as a single object for convenience
export const designTokens = {
  spacing,
  typography,
  colors,
  borderRadius,
  shadows,
  zIndex,
  breakpoints,
  media,
  layout,
  transitions,
  components,
} as const;

// Type exports
export type Spacing = keyof typeof spacing;
export type FontSize = keyof typeof typography.fontSize;
export type FontWeight = keyof typeof typography.fontWeight;
export type Color = keyof typeof colors;
export type OpacityColor = keyof typeof colors.withOpacity;
export type BorderRadius = keyof typeof borderRadius;
export type Shadow = keyof typeof shadows;
export type Breakpoint = keyof typeof breakpoints; // 'md' | 'lg' | 'mobile' | 'tablet' | 'desktop'
export type IconSize = keyof typeof components.icon.size;
export type MaxWidth = keyof typeof layout.maxWidth;
